import React, { useEffect, useState } from "react";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../store";
import { Site, SiteFormData } from "./site";
import { selectBranches, selectBranchLoading } from "../branch/branchSlice";
import BaseModal from "../../components/common/BaseModal";
import FormInput from "../../components/common/FormInput";
import Box from "@mui/material/Box";

import InputAdornment from "@mui/material/InputAdornment";
import EmailField from "../../form/EmailField";
import PhoneField from "../../form/PhoneField";
import ZipCodeField from "../../form/ZipCodeField";
import TextInputField from "../../form/TextInputField";
// import { createCustomer, editCustomer } from "./customerSlice";
import { editSite, createSite, selectSites } from "./siteSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomers, selectCustomers } from "../customer/customerSlice";
import { TextField, MenuItem, Autocomplete } from "@mui/material";
import StateSelect from "../../form/StateField";
import CitySelect from "../../form/CityField";
import CommonAddress from "../../form/CommonAddress";
import {
  stateOptions,
  allCities,
  cityToStateOptions,
} from "../../constants/locationOptions";
import { WhatsApp } from "@mui/icons-material";

interface SitesFormModalProps {
  isOpen: boolean;
  onRequestClose: (hasData: boolean) => void;
  sites: Site | null;
  customerId?: string | null;
  onSuccess: (site: Site) => void;
}

const SitesFormModal: React.FC<SitesFormModalProps> = ({
  isOpen,
  onRequestClose,
  sites,
  customerId,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const customers = useSelector(selectCustomers);
  const Sites = useSelector(selectSites);

  useEffect(() => {
    if (customers.length === 0) {
      dispatch(fetchCustomers({}) as any);
    }
  }, [customers.length, dispatch]);

  const isEditMode = !!sites;
  const [formData, setFormData] = useState<SiteFormData>({
    name: "",
    address: "",
    code:"",
    city: "",
    namePlate: "",
    state: "",
    zipCode: "",
    contactPerson: "",
    contactPhone: "",
    whatsapp: "",
    status: "",
    notes: "",
    customerId: "",
    totalOrder: 0,
    completedOrder: 0,
    pendingOrder: 0,
  });

  useEffect(() => {
    console.log("Sites data in modal:", sites);
    // if(isOpen){
    if (sites) {
      setFormData({
        name: sites.name || "",
        address: sites.address || "",
        city: sites.city || "",
        state: sites.state || "",
        zipCode: sites.zipCode || "",
        contactPerson: sites.contactPerson || "",
        contactPhone: sites.contactPhone || "",
        whatsapp: sites.whatsapp || "",
        status: "active",
        notes: "Main production facility",
        // customerId: sites.customerId || "",
        code: sites.code || "",
        namePlate: sites.namePlate || "",
        customerId: customerId || sites.customerId,
        totalOrder: 0,
        completedOrder: 0,
        pendingOrder: 0,
      });
    } else {
      setFormData({
        name: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        contactPerson: "",
        contactPhone: "",
        whatsapp: "",
        status: "",
        customerId: customerId || "",
        notes: "Main production facility",
        code:"",
        namePlate: "",
        totalOrder: 0,
        completedOrder: 0,
        pendingOrder: 0,
      });
    }
    // Clear errors when modal opens/closes
    setErrors({});
    setBackendErrors([]);
    setTouchedWhileTyping({
      whatsapp: false,
      phone: false,
      zipCode: false,
      contactPhone: false,
    });
  }, [isOpen, sites]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedCustomer, setSelectedCustomer] = React.useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [sameAsAboveWhatsapp, setSameAsAboveWhatsapp] = useState(false);
  const [backendErrors, setBackendErrors] = useState<string[]>([]);

  const [touchedWhileTyping, setTouchedWhileTyping] = useState({
    whatsapp: false,
    phone: false,
    zipCode: false,
    contactPhone: false,
  });

  useEffect(() => {
    if (sameAsAboveWhatsapp){
      setFormData(prev => ({
        ...prev,
        whatsapp: prev.contactPhone || "",
      }));
    }
  },[sameAsAboveWhatsapp,formData.whatsapp, formData.contactPhone])

  

  const handleCheckboxChangeWhatsapp = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSameAsAboveWhatsapp(e.target.checked);
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
      (customer.code &&
        customer.code.toLowerCase().includes(customerSearch.toLowerCase()))
  );

  // Filter states based on search term
  const filteredStates = stateOptions.filter((state) =>
    state.label.toLowerCase().includes(stateSearch.toLowerCase())
  );

  // Filter cities based on search term
  const filteredCities = allCities.filter((city) =>
    city.label.toLowerCase().includes(citySearch.toLowerCase())
  );



  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name === "contactPerson" && !/^[a-zA-Z\s]*$/.test(value)) return;

    if (!touchedWhileTyping[name as keyof typeof touchedWhileTyping]) {
      setTouchedWhileTyping((prev) => ({
        ...prev,
        [name]: true,
      }));
    }

    setFormData((prev) => {
      // Clear city if state is cleared
      if (name === "state" && (!value || value === "")) {
        return {
          ...prev,
          state: value,
          city: "",
        };
      }
      return {
        ...prev,
        [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
      };
    });
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    // Clear backend errors when user starts typing
    if (backendErrors.length > 0) {
      setBackendErrors([]);
    }
  };

  // Helper function to safely get trimmed string value
  const getTrimmedString = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "number") return String(value).trim();
    return String(value || "").trim();
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.customerId) {
      newErrors.customerId = "Customer is required";
    }
    if (!getTrimmedString(formData.name)) {
      newErrors.name = "Site name is required";
    }
    if (!getTrimmedString(formData.address)) {
      newErrors.address = "Address is required";
    }
    if (!getTrimmedString(formData.city)) {
      newErrors.city = "City is required";
    }
    if (!getTrimmedString(formData.state)) {
      newErrors.state = "State is required";
    }
    // Zip code is optional, but if provided, must be 6 digits
    const zipCode = getTrimmedString(formData.zipCode);
    if (zipCode && !/^\d{6}$/.test(zipCode)) {
      newErrors.zipCode = "Zip code must be exactly 6 digits";
    }
    if (!getTrimmedString(formData.contactPerson)) {
      newErrors.contactPerson = "Contact person is required";
    }
    const contactPhone = getTrimmedString(formData.contactPhone);
    if (!contactPhone) {
      newErrors.contactPhone = "Contact phone is required";
    } else if (!/^\d{10}$/.test(contactPhone)) {
      newErrors.contactPhone = "Phone number must be exactly 10 digits";
    }
    const whatsapp = getTrimmedString(formData.whatsapp);
    if (!whatsapp) {
      newErrors.whatsapp = "WhatsApp number is required";
    } else if (!/^\d{10}$/.test(whatsapp)) {
      newErrors.whatsapp = "WhatsApp number must be exactly 10 digits";
    }

    return newErrors;
  };

  const isFormValid = () => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0;
  };

  const hasFormData = (): boolean => {
    return !!(
      formData.name.trim() ||
      formData.address.trim() ||
      formData.code.trim() ||
      formData.namePlate.trim() ||
      formData.state.trim() ||
      formData.city.trim() ||
      formData.state.trim() ||
      formData.zipCode.trim() ||
      formData.contactPerson.trim() ||
      // formData.customerId.trim() ||
      // formData.totalOrder ||
      // formData.completedOrder ||
      // formData.pendingOrder ||
      formData.status.trim() ||
      // formData.notes.trim() ||
      formData.whatsapp.trim() 
    );
  };

  useEffect(()=>{
    console.log("hasFormData--->",hasFormData)
    console.log("formData--->",formData)
  },[hasFormData,formData])

  const handleSubmit = async (e: React.FormEvent) => {
    console.log("submited data is", JSON.stringify(formData));
    e.preventDefault();
    
    // Clear previous backend errors
    setBackendErrors([]);
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Mark all fields as touched to show errors
      setTouchedWhileTyping({
        whatsapp: true,
        phone: true,
        zipCode: true,
        contactPhone: true,
      });
      return;
    }
    
    setIsSubmitting(true);

    // Prepare payload - remove empty strings for optional fields and set defaults
    const payload: SiteFormData = {
      ...formData,
      // Ensure status has a default value
      status: formData.status || "active",
      // Trim all string fields
      name: formData.name?.trim() || "",
      address: formData.address?.trim() || "",
      city: formData.city?.trim() || "",
      state: formData.state?.trim() || "",
      contactPerson: formData.contactPerson?.trim() || "",
      contactPhone: formData.contactPhone?.trim() || "",
      whatsapp: formData.whatsapp?.trim() || "",
      // Remove empty strings for optional fields (convert to undefined)
      zipCode: formData.zipCode?.trim() || undefined,
      notes: formData.notes?.trim() || undefined,
      code: formData.code?.trim() || undefined,
      namePlate: formData.namePlate?.trim() || undefined,
    };

    try {
      if (isEditMode) {
        const response = await dispatch(
          editSite({ id: sites.id, siteData: payload }) as any
        );
        console.log(
          "..................Response after updating site:............",
          response?.payload
        );
        console.log("Updating Site with Data:", JSON.stringify(payload));
        
        if (response.type === 'sites/editSite/rejected') {
          console.error("Site update failed:", response.payload);
          // Handle backend validation errors
          if (response.payload?.errors && Array.isArray(response.payload.errors)) {
            setBackendErrors(response.payload.errors);
          } else if (response.payload?.message) {
            setBackendErrors([response.payload.message]);
          } else {
            setBackendErrors(["Failed to update site. Please try again."]);
          }
          return; // Don't close modal on error
        }
        
        onSuccess(response?.payload);
      } else {
        const response = await dispatch(createSite(payload) as any);
        console.log(
          "..................Response after creating site:............",
          response?.payload
        );
        console.log("Creating Site with Data:", JSON.stringify(payload));
        
        if (response.type === 'sites/createSite/rejected') {
          console.error("Site creation failed:", response.payload);
          // Handle backend validation errors
          if (response.payload?.errors && Array.isArray(response.payload.errors)) {
            setBackendErrors(response.payload.errors);
          } else if (response.payload?.message) {
            setBackendErrors([response.payload.message]);
          } else {
            setBackendErrors(["Failed to create site. Please try again."]);
          }
          return; // Don't close modal on error
        }
        
        onSuccess(response?.payload);
      }
      return; // Close via parent state change, don't trigger onClose
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setBackendErrors(["An unexpected error occurred. Please try again."]);
      // Don't close the modal or clear the form on error
      return;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => onRequestClose(hasFormData())}
      ></div>

      {/* Slide-in Panel */}
      <div
        className={`absolute right-0 top-0 h-full w-[30%] bg-white shadow-xl transform transition-transform duration-1000 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? "Edit Site" : "Create Site"}
            </h2>
            <button
              onClick={() => onRequestClose(hasFormData())}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <Autocomplete
                options={filteredCustomers}
                getOptionLabel={(option) =>
                  option.name
                    ? `${option.name}`
                    : ""
                }
                value={
                  customers.find((c) => c.id === formData.customerId) || null
                }
                onChange={(_, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    customerId: value ? value.id : "",
                  }))
                }
                inputValue={customerSearch}
                onInputChange={(_, value) => setCustomerSearch(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customer"
                    variant="outlined"
                    fullWidth
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{ mb: 2 }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <div>
                      <div>{option.name}</div>
                      {/* {option.code && (
                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                          Code: {option.code}
                        </div>
                      )} */}
                    </div>
                  </li>
                )}
              />

              {/* Name */}
              <TextInputField
                label="Site Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
              />

              <TextInputField
                label="Name Plate"
                name="namePlate"
                value={formData.namePlate}
                onChange={handleChange}
                required
                icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
              />

              {/* Common Address */}
              {/* <CommonAddress
                formData={formData}
                onChange={handleChange}
                touchedWhileTyping={touchedWhileTyping}
              /> */}
              <TextInputField
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
              />

              {/* <Autocomplete
                options={filteredCities}
                getOptionLabel={(option) => option.label}
                value={
                  allCities.find((city) => city.value === formData.city) || null
                }
                onChange={(_, value) => {
                  const newCity = value ? value.value : "";
                  const newState = value
                    ? cityToStateOptions[value.value] || ""
                    : "";
                  setFormData((prev) => ({
                    ...prev,
                    city: newCity,
                    state: newState,
                  }));
                }}
                inputValue={citySearch}
                onInputChange={(_, value) => setCitySearch(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    variant="outlined"
                    fullWidth
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                sx={{ mb: 2 }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <div>
                      <div>{option.label}</div>
                      {cityToStateOptions[option.value] && (
                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                          {cityToStateOptions[option.value]}
                        </div>
                      )}
                    </div>
                  </li>
                )}
              /> */}
              <CitySelect
                state={formData.state}
                value={formData.city}
                onChange={handleChange}
                onStateChange={(stateName, stateId) => {
                  setFormData(prev => ({
                    ...prev,
                    state: stateName,
                  }));
                }}
                required
              />

              <StateSelect
                value={formData.state}
                onChange={handleChange}
                required
              />

              {/* <Autocomplete
                options={filteredStates}
                getOptionLabel={(option) => option.label}
                value={
                  stateOptions.find(
                    (state) => state.value === formData.state
                  ) || null
                }
                onChange={(_, value) => {
                  setFormData((prev) => ({
                    ...prev,
                    state: value ? value.value : "",
                    // Clear city when state changes
                    city: "",
                  }));
                  // Clear city search when state changes
                  setCitySearch("");
                }}
                inputValue={stateSearch}
                onInputChange={(_, value) => setStateSearch(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    variant="outlined"
                    fullWidth
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                sx={{ mb: 2 }}
              /> */}
              

              <ZipCodeField
                label="ZIP Code"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                showError={touchedWhileTyping.zipCode}
              />

              {/* Contact Person */}
              <p>Contact Person</p>
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
              <TextInputField
                label="Contact Person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                required
                icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
              />
              <PhoneField
                label="Contact Phone"
                name="contactPhone"
                value={formData.contactPhone || ""}
                onChange={handleChange}
                required
                showError={touchedWhileTyping.contactPhone}
              />
              <div className="flex items-center gap-1">
                <input
                  type="checkbox"
                  id="sameAsAboveWhatsapp"
                  checked={sameAsAboveWhatsapp}
                  onChange={handleCheckboxChangeWhatsapp}
                  className="h-4 w-4"
                />
                <label
                  htmlFor="sameAsAbove"
                  className="text-gray-600 text-sm cursor-pointer"
                >
                  Same as above
                </label>
              </div>
              <PhoneField
                label="WhatsApp"
                name="whatsapp"
                value={formData.whatsapp || ""}
                onChange={handleChange}
                required
                showError={touchedWhileTyping.whatsapp}
              />
              {/* </div> */}

              {/* Submit Error */}
              {errors.submit && (
                <p className="text-sm text-red-500 mt-2">{errors.submit}</p>
              )}

              {/* Backend Validation Errors */}
              {backendErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-2">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Validation Error{backendErrors.length > 1 ? 's' : ''}
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <ul className="list-disc list-inside space-y-1">
                          {backendErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex flex-wrap justify-between gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => onRequestClose(hasFormData())}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid()}
                  className="inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditMode ? (
                    "Update Site"
                  ) : (
                    "Create Site"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitesFormModal;

// import React from "react";
// import TextInputField from "../../form/TextInputField";
// import  PhoneField  from "../../form/PhoneField";
// import  ZipCodeField  from "../../form/ZipCodeField";
// import BaseModal from "../../components/common/BaseModal";
// import {
//   IdentificationIcon,
//   MapPinIcon,
//   PhoneIcon,
//   EnvelopeIcon,
// } from "@heroicons/react/24/outline";

// type SiteFormModalProps = {
//   isOpen: boolean;
//   onClose: () => void;
//   isEditMode?: boolean;
// };

// const SetFormModal: React.FC<SiteFormModalProps> = ({
//   isOpen,
//   onClose,
//   isEditMode = false,
// }) => {
//   const handleSubmit = (e: React.FormEvent) => {
//     e.preventDefault();
//     // placeholder
//   };

//   const handleChange = () => {
//     // placeholder
//   };

//   const formData = {
//     name: "",
//     address: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     contactPerson: "",
//     contactPhone: "",
//     contactEmail: "",
//     branchId: "",
//   };

//   const touchedWhileTyping = {
//     zipCode: false,
//     contactPhone: false,
//     contactEmail: false,
//   };

//   const errors = {
//     submit: "",
//   };

//   const isFormValid = true;

//   return (
//     <BaseModal
//       isOpen={isOpen}
//       onClose={onClose}
//       title={isEditMode ? "Edit Site" : "Create Site"}
//     >
//       <form onSubmit={handleSubmit} className="space-y-4">
//         {/* Name */}
//         {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
//           <TextInputField
//             label="Name"
//             name="name"
//             value={formData.name}
//             onChange={handleChange}
//             required
//             icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
//           />
//         {/* </div> */}

//         {/* Address */}
//         <TextInputField
//           label="Address"
//           name="address"
//           value={formData.address}
//           onChange={handleChange}
//           required
//           icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
//         />

//         {/* City, State, ZIP */}
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//           <TextInputField
//             label="City"
//             name="city"
//             value={formData.city}
//             onChange={handleChange}
//             required
//             icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
//           />
//           <TextInputField
//             label="State"
//             name="state"
//             value={formData.state}
//             onChange={handleChange}
//             required
//             icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
//           />
//           <ZipCodeField
//             label="ZIP Code"
//             name="zipCode"
//             value={formData.zipCode}
//             onChange={handleChange}
//             showError={touchedWhileTyping.zipCode}
//           />
//         </div>

//         <p className="text-base font-medium mt-4">Contact Person</p>

//         {/* Contact Person Fields */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//           <TextInputField
//             label="Contact Person"
//             name="contactPerson"
//             value={formData.contactPerson}
//             onChange={handleChange}
//             required
//             icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
//           />
//           <PhoneField
//             label="Contact Phone"
//             name="contactPhone"
//             value={formData.contactPhone}
//             onChange={handleChange}
//             required
//             showError={touchedWhileTyping.contactPhone}
//           />
//         </div>

//         {errors.submit && (
//           <p className="text-sm text-red-500 dark:text-red-400 mt-2">
//             {errors.submit}
//           </p>
//         )}

//         {/* Buttons */}
//         <div className="flex flex-wrap justify-between gap-3 mt-6">
//           <button
//             type="button"
//             onClick={onClose}
//             className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             className={`inline-flex items-center mx-4 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
//               isFormValid
//                 ? "bg-primary-600 hover:bg-primary-700"
//                 : "bg-gray-400 cursor-not-allowed"
//             }`}
//             disabled={!isFormValid}
//           >
//             {isEditMode ? "Update Site" : "Create Site"}
//           </button>
//         </div>
//       </form>
//     </BaseModal>
//   );
// };

// export default SetFormModal;
